import React from 'react';

const INLINE_RE = /(\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|`([^`\n]+)`)/g;
const BULLET_LINE_RE = /^\s*[-*]\s+/;
const NUMBERED_LINE_RE = /^\s*\d+\.\s+/;

const renderInline = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  for (const match of Array.from(text.matchAll(INLINE_RE))) {
    const matchIdx = match.index ?? 0;
    if (matchIdx > lastIndex) nodes.push(text.slice(lastIndex, matchIdx));
    if (match[2] !== undefined) nodes.push(<strong key={nodes.length}>{match[2]}</strong>);
    else if (match[3] !== undefined) nodes.push(<em key={nodes.length}>{match[3]}</em>);
    else if (match[4] !== undefined) nodes.push(<code key={nodes.length}>{match[4]}</code>);
    lastIndex = matchIdx + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

export interface IChatbotMessageProps {
  text: string;
}

export const ChatbotMessage: React.FC<IChatbotMessageProps> = ({ text }) => {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    if (lines[i].trim() === '') {
      i += 1;
      continue;
    }

    if (BULLET_LINE_RE.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && BULLET_LINE_RE.test(lines[i])) {
        items.push(lines[i].replace(BULLET_LINE_RE, ''));
        i += 1;
      }
      blocks.push(
        <ul key={blocks.length}>
          {items.map((item, idx) => <li key={idx}>{renderInline(item)}</li>)}
        </ul>
      );
      continue;
    }

    if (NUMBERED_LINE_RE.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && NUMBERED_LINE_RE.test(lines[i])) {
        items.push(lines[i].replace(NUMBERED_LINE_RE, ''));
        i += 1;
      }
      blocks.push(
        <ol key={blocks.length}>
          {items.map((item, idx) => <li key={idx}>{renderInline(item)}</li>)}
        </ol>
      );
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length
      && lines[i].trim() !== ''
      && !BULLET_LINE_RE.test(lines[i])
      && !NUMBERED_LINE_RE.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={blocks.length}>
        {paraLines.flatMap((line, idx) => (
          idx === 0
            ? renderInline(line)
            : [<br key={`br-${idx}`} />, ...renderInline(line)]
        ))}
      </p>
    );
  }

  return <>{blocks}</>;
};
